import JSZip from 'jszip';

export interface DownloadableFile {
  url: string;
  filename: string;
  mimeType?: string;
}

export interface DownloadProgress {
  current: number;
  total: number;
  filename?: string;
}

/**
 * Downloads a file from a URL and returns the blob
 */
export const downloadFile = async (url: string): Promise<Blob> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download file: ${response.statusText}`);
  }
  return response.blob();
};

/**
 * Extracts a filename from a URL
 */
export const getFilenameFromUrl = (url: string, fallback?: string): string => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const filename = pathname.split('/').pop() || '';
    
    if (filename && filename.includes('.')) {
      return filename;
    }
    
    // If no extension found, try to get from search params
    const disposition = urlObj.searchParams.get('filename');
    if (disposition) {
      return disposition;
    }
    
    return fallback || `file-${Date.now()}`;
  } catch {
    return fallback || `file-${Date.now()}`;
  }
};

/**
 * Ensures all filenames in the array are unique by appending numbers if needed
 */
export const ensureUniqueFilenames = (files: DownloadableFile[]): DownloadableFile[] => {
  const seen = new Map<string, number>();
  
  return files.map(file => {
    let filename = file.filename;
    const count = seen.get(filename) || 0;
    
    if (count > 0) {
      const parts = filename.split('.');
      if (parts.length > 1) {
        const extension = parts.pop();
        const name = parts.join('.');
        filename = `${name}-${count}.${extension}`;
      } else {
        filename = `${filename}-${count}`;
      }
    }
    
    seen.set(file.filename, count + 1);
    
    return {
      ...file,
      filename
    };
  });
};

/**
 * Downloads multiple files and creates a ZIP archive
 */
export const downloadFilesAsZip = async (
  files: DownloadableFile[],
  zipFilename: string,
  onProgress?: (progress: DownloadProgress) => void
): Promise<void> => {
  const zip = new JSZip();
  
  // Download all files and add to zip
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    
    onProgress?.({
      current: i + 1,
      total: files.length,
      filename: file.filename
    });
    
    try {
      const blob = await downloadFile(file.url);
      zip.file(file.filename, blob);
    } catch (error) {
      console.warn(`Failed to download ${file.filename}:`, error);
      // Continue with other files even if one fails
    }
  }
  
  // Generate ZIP file
  const zipBlob = await zip.generateAsync({ type: 'blob' });
  
  // Create download link
  const url = URL.createObjectURL(zipBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = zipFilename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
